import { Alert } from '@servicetitan/anvil2';
import { Calculation } from '@servicetitan/financing-api-client';
import { formatCurrency } from '@servicetitan/financing-utils-shared';
import { PreApprovalCard } from './PreApprovalCard';
import { PreApprovalDisplay } from './usePreApprovalDisplay';

const CURRENCY_OPTIONS: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
};

const buildOutOfRangeTitle = (
    calculation: Calculation,
    preApprovalAmount: number,
    hasOtherPlans: boolean,
): string => {
    const min = formatCurrency(calculation.minLoanAmount, CURRENCY_OPTIONS);
    // GreenSky leaves maxLoanAmount unset (0); the practical upper bound is the pre-approval limit.
    const upperBound = Math.min(preApprovalAmount, calculation.maxLoanAmount || Infinity);
    const max = formatCurrency(upperBound, CURRENCY_OPTIONS);
    const tail = hasOtherPlans ? 'Adjust the amount or choose a plan below.' : 'Adjust the amount.';
    return `Your pre-approved offer applies to financed amounts between ${min}–${max}. ${tail}`;
};

interface PreApprovalSectionProps {
    /** The resolved pre-approval display state; only the renderable kinds carry a calculation. */
    display: Extract<PreApprovalDisplay, { kind: 'visible' | 'outOfRange' }>;
    /** Whether the selector below lists other plans (drives the out-of-range alert's tail clause). */
    hasOtherPlans: boolean;
    /** Wrapped by the orchestrator to set `setupData.preApprovalId` before delegating to the workflow. */
    onPlanSelected?: (plan: Calculation) => void;
}

/**
 * The GreenSky pre-approval section: the out-of-range warning (out of range only) above the
 * {@link PreApprovalCard}. Rendered as the plans region's leading content so both sit inside the
 * `.plans-container` container-query context that the card and the selector grid share.
 */
export const PreApprovalSection = ({
    display,
    hasOtherPlans,
    onPlanSelected,
}: PreApprovalSectionProps) => (
    <>
        {display.kind === 'outOfRange' && (
            <Alert
                status="warning"
                title={buildOutOfRangeTitle(
                    display.calculation,
                    display.preApproval.amount!,
                    hasOtherPlans,
                )}
            />
        )}
        <PreApprovalCard display={display} onPlanSelected={onPlanSelected} />
    </>
);
